import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, Button, Card, Dialog, Field, Input, Spinner } from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../api/hooks";
import type { ExportJob, Schema } from "../api/types";
import { fieldErrorsOf, useHandleApiError } from "../errors";
import { daysUntil, formatDate } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { stepUp } from "../teams";
import { Centered } from "./Onboarding";

type Lifecycle = Schema<"LifecycleStatus">;

/** Banner above every page for the states that keep the app usable but limited (04 §6 tenant banners). */
export function StateBanner() {
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const tz = s.me.tenant.timeZone;
  const days = daysUntil(s.me.tenant.purgeScheduledFor, new Date());
  if (s.tenantState === "purge_scheduled")
    return (
      <Banner
        tone="danger"
        action={
          s.can("SETTINGS_EDIT") ? (
            <Button onClick={() => nav("/account")}>{t("ui:state.cancelPurge")}</Button>
          ) : undefined
        }
      >
        {t("ui:state.purgeScheduled", {
          date: formatDate(s.me.tenant.purgeScheduledFor, locale, tz),
          days: days ?? 0,
        })}
      </Banner>
    );
  if (s.tenantState === "consent_lost")
    return (
      <Banner
        tone="danger"
        action={<Button onClick={() => nav("/settings")}>{t("ui:state.reconsent")}</Button>}
      >
        {t("ui:state.consentLost")}
      </Banner>
    );
  if (s.tenantState === "read_only")
    return (
      <Banner
        tone="warn"
        action={
          s.can("SUBSCRIPTION_READ") ? (
            <Button onClick={() => nav("/subscription")}>{t("ui:state.resubscribe")}</Button>
          ) : undefined
        }
      >
        {t("common:readOnlyBanner")}
      </Banner>
    );
  if (s.me.tenant.suspension === "suspended") return <Banner tone="warn">{t("ui:state.suspended")}</Banner>;
  return null;
}

/** Full-screen pages of the states where almost nothing is allowed: blocked, archived, uninstalled, purge scheduled (BR-172, BR-175, BR-252). */
export function AccountState() {
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const fail = useHandleApiError();
  const lifecycle = useGet<Lifecycle>("/api/v1/lifecycle");
  const exportReq = useWrite<ExportJob>("POST", "/api/v1/lifecycle/export-request", ["/api/v1/lifecycle"]);
  const cancel = useWrite("DELETE", "/api/v1/lifecycle/purge-request/:requestId", [
    "/api/v1/lifecycle",
    "/api/v1/me",
  ]);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [exportSent, setExportSent] = useState(false);
  const tz = s.me.tenant.timeZone;

  if (s.me.tenant.suspension === "blocked")
    return (
      <Centered>
        <Card title={t("common:blockedTitle")} tone="danger">
          <p className="t-body-lg" style={{ margin: 0 }}>
            {t("common:blockedBody")}
          </p>
        </Card>
      </Centered>
    );
  if (lifecycle.isLoading)
    return (
      <Centered>
        <Spinner label={t("common:loading")} />
      </Centered>
    );
  const l = lifecycle.data;
  const purge = l?.purge;
  const state = s.tenantState;
  const title =
    state === "uninstalled"
      ? t("ui:state.uninstalledTitle")
      : state === "purge_scheduled"
        ? t("ui:state.purgeTitle")
        : t("ui:state.closedTitle");
  const body =
    state === "uninstalled"
      ? t("ui:state.uninstalledBody", { date: formatDate(purge?.scheduledFor, locale, tz) })
      : state === "purge_scheduled"
        ? t("ui:state.purgeBody", { date: formatDate(purge?.scheduledFor, locale, tz) })
        : t("ui:state.closedBody");

  return (
    <Centered>
      <Card title={title} tone={state === "purge_scheduled" ? "danger" : undefined}>
        <p className="t-body-lg" style={{ margin: 0 }}>
          {body}
        </p>
        {purge && state !== "purge_scheduled" ? (
          <p className="t-body gp-muted" style={{ margin: 0 }}>
            {t("ui:state.longStop", { date: formatDate(purge.scheduledFor, locale, tz) })}
          </p>
        ) : null}
        <div className="gp-actions">
          {l?.canResubscribe && s.can("SUBSCRIPTION_READ") ? (
            <Button variant="primary" onClick={() => nav("/subscription")}>
              {t("ui:state.resubscribe")}
            </Button>
          ) : null}
          {state === "archived" ? (
            <Button
              disabledReason={s.can("LIFECYCLE_EXPORT") ? undefined : t("ui:gate.noPermission")}
              onClick={() =>
                exportReq.mutate(undefined, { onSuccess: () => setExportSent(true), onError: fail })
              }
            >
              {t("ui:state.requestExport")}
            </Button>
          ) : null}
          {state === "purge_scheduled" && purge?.cancellable && purge.id ? (
            <Button
              variant="primary"
              disabledReason={s.can("SETTINGS_EDIT") ? undefined : t("ui:gate.noPermission")}
              onClick={() => cancel.mutate({ params: { requestId: purge.id! } }, { onError: fail })}
            >
              {t("ui:state.cancelPurge")}
            </Button>
          ) : null}
          {state !== "purge_scheduled" ? (
            <Button
              variant="danger"
              disabledReason={s.isPrimaryAdmin ? undefined : t("ui:state.purgePrimaryOnly")}
              onClick={() => setPurgeOpen(true)}
            >
              {t("ui:state.requestPurge")}
            </Button>
          ) : null}
        </div>
        {exportSent ? <Banner tone="success">{t("ui:state.exportSent")}</Banner> : null}
      </Card>
      <PurgeDialog open={purgeOpen} onClose={() => setPurgeOpen(false)} tenantName={s.me.tenant.name} />
    </Centered>
  );
}

/** Purge request: re-authentication, the typed organisation name and a final export first (BR-175, BR-228). */
export function PurgeDialog({
  open,
  onClose,
  tenantName,
}: {
  open: boolean;
  onClose: () => void;
  tenantName: string;
}) {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const exportReq = useWrite<ExportJob>("POST", "/api/v1/exports", ["/api/v1/exports"]);
  const request = useWrite<Lifecycle, { tenantNameConfirmation: string; finalExportJobId?: string }>(
    "POST",
    "/api/v1/lifecycle/purge-request",
    ["/api/v1/lifecycle", "/api/v1/me"],
  );
  const [finalExport, setFinalExport] = useState<string | undefined>();

  const offerExport = () =>
    exportReq.mutate(
      { body: { scope: "all", format: "xlsx" } },
      { onSuccess: (j) => setFinalExport(j.id), onError: fail },
    );
  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      const token = await stepUp(); // sign in again right before (auth_time within 10 minutes)
      request.mutate(
        {
          body: { tenantNameConfirmation: name, ...(finalExport ? { finalExportJobId: finalExport } : {}) },
          token,
        },
        {
          onSuccess: () => {
            onClose();
            void s.refresh();
          },
          onError: (e) => {
            setErrors(fieldErrorsOf(e));
            fail(e);
          },
        },
      );
    } catch {
      /* the sign-in window was closed */
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      title={t("ui:purge.title")}
      description={t("ui:purge.body")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="danger"
            disabledReason={
              name.trim() === tenantName ? undefined : t("ui:purge.typeName", { name: tenantName })
            }
            onClick={() => void submit()}
          >
            {t("ui:purge.confirm")}
          </Button>
        </>
      }
    >
      <Banner tone="warn">{t("ui:purge.irreversible")}</Banner>
      <div className="gp-row">
        <Button onClick={offerExport}>{t("ui:purge.exportFirst")}</Button>
        {finalExport ? (
          <span className="t-body-sm gp-muted" role="status">
            {t("ui:purge.exportQueued")}
          </span>
        ) : null}
      </div>
      <Field label={t("ui:purge.nameLabel", { name: tenantName })} error={errors["tenantNameConfirmation"]}>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      </Field>
    </Dialog>
  );
}
